import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteTechnicianAwayStatus, getTechnicianAwayStatuses, putTechnicianAwayStatus } from 'queries';
import { parseErrors, Toast } from 'helpers';
import { JsonDataItem } from 'types';
import { AVAILABILITY_SAVED_MESSAGE } from 'pages/technician/edit/hooks/useTechnicianAvailabilityCalendar';

export const AVAILABILITY_DELETED_MESSAGE = 'Availability has been deleted.';

const showError = (error: JsonDataItem) => {
  Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
};

export const useTechnicianAwayStatuses = (technicianId?: string) => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(5);

  const {
    data,
    isInitialLoading: isAwayStatusesLoading,
    refetch,
  } = useQuery(
    ['technician-away-statuses', { technicianId, page: currentPage, per_page: recordsPerPage }],
    getTechnicianAwayStatuses,
    {
      enabled: !!technicianId,
      onError: showError,
    },
  );

  useEffect(() => {
    if (data && data.data.length === 0 && currentPage > 1) {
      setCurrentPage(Math.max(1, Math.ceil(data.meta.total / recordsPerPage)));
    }
  }, [data, currentPage, recordsPerPage]);

  const refreshAfterChange = (message: string) => {
    Toast.show({ description: message, type: Toast.Type.Success });
    queryClient.invalidateQueries(['technician-availability-calendar']);

    return refetch();
  };

  const { mutate: updateAwayStatus, isLoading: isAwayStatusUpdating } = useMutation(putTechnicianAwayStatus, {
    onSuccess: () => refreshAfterChange(AVAILABILITY_SAVED_MESSAGE),
    onError: showError,
  });

  const { mutate: deleteAwayStatus, isLoading: isAwayStatusDeleting } = useMutation(deleteTechnicianAwayStatus, {
    onSuccess: () => refreshAfterChange(AVAILABILITY_DELETED_MESSAGE),
    onError: showError,
  });

  const paginationMetadata = {
    current_page: data?.meta?.current_page || 1,
    total: data?.meta?.total || 0,
    pageSize: data?.meta?.per_page || 5,
    onChange: (page: number, pageSize: number) => {
      setCurrentPage(page);
      setRecordsPerPage(pageSize);
    },
  };

  return {
    awayStatuses: data?.data,
    isAwayStatusesLoading,
    paginationMetadata,
    updateAwayStatus,
    isAwayStatusUpdating,
    deleteAwayStatus,
    isAwayStatusDeleting,
  };
};
